// Vector port of TournamentPrintableTable.js's renderParticipants().
import { t } from "i18next";
import { PRINT_THEME as T } from "./printTheme";
import { COUNTRY_OPTIONS } from "../../../constants";
import { getOptionName } from "../../../utils/getOptionName";
import { formatEntryListName, formatEntryListDoublesPair, formatTeamMembers } from "./exportUtils";
import { px2pt, px2mm, lineHeightMm, drawTableRow, hLine, drawText } from "./pdfKit";
import { preloadFlags } from "./pdfFlag";

export const CONTENT_WIDTH_PX = 900;
export const CONTENT_WIDTH_MM = px2mm(CONTENT_WIDTH_PX);

const HEADER_FS = px2pt(13);
const BODY_FS = px2pt(13);
const SUB_FS = px2pt(11);
const HEADER_H = lineHeightMm(HEADER_FS, 1.2) + px2mm(16);
const ROW_H_1 = lineHeightMm(BODY_FS, 1.3) + px2mm(14);
const ROW_H_2 = ROW_H_1 + lineHeightMm(SUB_FS, 1.3) + px2mm(2);

const NUM_W = px2mm(40);
const RATING_W = px2mm(100);
const GEO_W = px2mm(260);
const NAME_W = CONTENT_WIDTH_MM - NUM_W - RATING_W - GEO_W;
const FLAG_W = px2mm(20);

const buildRows = (list, grade) => list.map((item, index) => {
    let name = "—", cityCountry = "", rating = "—", flagCode = null, membersLine = "";
    if (grade === 1) {
        name = formatEntryListName(item);
        flagCode = item?.country || null;
        const countryStr = item?.country ? t(getOptionName(COUNTRY_OPTIONS, item.country)) : null;
        cityCountry = [item?.city, countryStr && !/^\d+$/.test(String(countryStr).trim()) ? countryStr : null].filter(Boolean).join(", ");
        rating = item?.rating != null ? item.rating : "—";
    } else if (grade === 2) {
        const p1 = item?.players?.[0];
        const p2 = item?.players?.[1];
        name = formatEntryListDoublesPair(p1, p2);
        const rawCountry = p1?.country || p2?.country;
        flagCode = rawCountry || null;
        const countryStr = rawCountry ? t(getOptionName(COUNTRY_OPTIONS, rawCountry)) : null;
        cityCountry = [p1?.city || p2?.city, countryStr && !/^\d+$/.test(String(countryStr).trim()) ? countryStr : null].filter(Boolean).join(", ");
        const pairSum = (p1?.rating || 0) + (p2?.rating || 0);
        rating = item?.team_rating != null ? item.team_rating : (pairSum > 0 ? pairSum : "—");
    } else {
        name = item?.name || `${t("Team")} ${index + 1}`;
        membersLine = formatTeamMembers(item?.players);
        rating = item?.team_rating || "—";
    }
    return { num: index + 1, name, cityCountry, rating, flagCode, membersLine, alt: index % 2 === 1 };
});

export const preloadParticipantsAssets = async (data, grade) => {
    const list = Array.isArray(data) ? data : (data?.results || []);
    const rows = buildRows(list, grade);
    const flagMap = await preloadFlags(rows.map(r => r.flagCode));
    return { flagMap };
};

export const measureParticipantsTable = (data, grade) => {
    const list = Array.isArray(data) ? data : (data?.results || []);
    if (!list.length) return px2mm(40);
    const rows = buildRows(list, grade);
    return HEADER_H + rows.reduce((sum, r) => sum + (r.membersLine ? ROW_H_2 : ROW_H_1), 0);
};

export const drawParticipantsTable = (pdf, x, y, data, grade, flagMap = {}) => {
    const list = Array.isArray(data) ? data : (data?.results || []);
    let cy = y;

    if (!list.length) {
        pdf.setFillColor(T.superWhite);
        pdf.rect(x, cy, CONTENT_WIDTH_MM, px2mm(40), "F");
        drawText(pdf, t("No data available"), x + CONTENT_WIDTH_MM / 2, cy + px2mm(15), { fontSizePt: BODY_FS, color: T.muted, align: "center" });
        return cy + px2mm(40);
    }

    const headerCols = [
        { widthMm: NUM_W, align: "center", text: "№" },
        { widthMm: NAME_W, align: "left", text: t("Participant"), padXMm: px2mm(10) },
        { widthMm: GEO_W, align: "left", text: t("City / Country"), padXMm: px2mm(10) },
        { widthMm: RATING_W, align: "center", text: t("Rating") },
    ];
    hLine(pdf, x, cy, CONTENT_WIDTH_MM, T.border, px2mm(1));
    drawTableRow(pdf, { x, y: cy, totalWidthMm: CONTENT_WIDTH_MM, cols: headerCols, rowHeightMm: HEADER_H, bg: T.superWhite, borderColor: T.border, fontSizePt: HEADER_FS, fontName: pdf.__pdfFontName, textColor: T.ink });
    cy += HEADER_H;

    const rows = buildRows(list, grade);
    rows.forEach((r) => {
        const rowH = r.membersLine ? ROW_H_2 : ROW_H_1;

        const cols = [
            { widthMm: NUM_W, align: "center", text: r.num, color: T.ink },
            {
                widthMm: NAME_W,
                customDraw: (pdf2, cx, cyy, w) => {
                    pdf2.setFont(pdf2.__pdfFontName || "PTSans", "normal");
                    pdf2.setFontSize(BODY_FS);
                    pdf2.setTextColor(T.ink);
                    const bodyLh = lineHeightMm(BODY_FS, 1.2);
                    const nameY = r.membersLine ? cyy + rowH / 2 - bodyLh * 0.15 : cyy + rowH / 2 + BODY_FS * 0.352778 * 0.32;
                    pdf2.text(r.name, cx + px2mm(10), nameY);
                    if (r.membersLine) {
                        pdf2.setFont(pdf2.__pdfFontName || "PTSans", "normal");
                        pdf2.setFontSize(SUB_FS);
                        pdf2.setTextColor(T.muted);
                        pdf2.text(r.membersLine, cx + px2mm(10), cyy + rowH / 2 + bodyLh * 0.85);
                    }
                },
            },
            {
                widthMm: GEO_W,
                customDraw: (pdf2, cx, cyy, w) => {
                    const flagUrl = r.flagCode ? flagMap[r.flagCode.toLowerCase()] : null;
                    let textX = cx + px2mm(10);
                    if (flagUrl) {
                        const fh = px2mm(14);
                        const fw = FLAG_W;
                        try { pdf2.addImage(flagUrl, "PNG", textX, cyy + rowH / 2 - fh / 2, fw, fh); } catch (e) { /* skip broken flag */ }
                        textX += fw + px2mm(6);
                    }
                    pdf2.setFont(pdf2.__pdfFontName || "PTSans", "normal");
                    pdf2.setFontSize(BODY_FS);
                    pdf2.setTextColor(T.muted);
                    pdf2.text(r.cityCountry || "—", textX, cyy + rowH / 2 + BODY_FS * 0.352778 * 0.32);
                },
            },
            { widthMm: RATING_W, align: "center", text: r.rating, bold: true, color: T.ink },
        ];

        drawTableRow(pdf, { x, y: cy, totalWidthMm: CONTENT_WIDTH_MM, cols, rowHeightMm: rowH, bg: r.alt ? T.superWhite : T.white, borderColor: T.border, fontSizePt: BODY_FS, fontName: pdf.__pdfFontName, textColor: T.ink });
        cy += rowH;
    });

    return cy;
};
